<template>
  <div class="attendance-manager">
    <!-- Loading State -->
    <div v-if="participantsLoading" class="text-center py-4">
      <alert class="m-4" />
    </div>

    <!-- Content -->
    <div v-else>
      <!-- ==================== CARD ROWS LAYOUT ==================== -->
      <template v-if="useCardLayout">
        <!-- Bulk Actions (compact) -->
        <div v-if="!disabled" class="mb-3">
          <!-- Mobile: Two rows of equal-width buttons -->
          <template v-if="isMobile">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <button
                type="button"
                class="btn btn-success btn-sm mr-2 flex-fill"
                :disabled="!participants.length"
                @click="markAllPresent"
              >
                <i class="fas fa-check-double mr-1"></i> All Present
              </button>
              <button
                type="button"
                class="btn btn-danger btn-sm flex-fill"
                :disabled="!participants.length"
                @click="markAllAbsent"
              >
                <i class="fas fa-times mr-1"></i> All Absent
              </button>
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <button
                type="button"
                class="btn btn-primary btn-sm mr-2 flex-fill"
                @click="openAddParticipants"
              >
                <i class="fas fa-plus mr-1"></i> Add {{ participantLabel }}s
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm flex-fill"
                :class="{ 'btn-pulse': hasUnsavedChanges }"
                :disabled="!hasUnsavedChanges || attendanceLoading"
                @click="saveAttendance"
              >
                <i
                  class="fas fa-spin fa-circle-notch"
                  v-if="attendanceLoading"
                ></i>
                <i class="fas fa-save mr-1" v-else></i> Save
                <span v-if="unsavedCount > 0">({{ unsavedCount }})</span>
              </button>
            </div>
          </template>
          <!-- Sidebar: Compact single row -->
          <div v-else class="d-flex justify-content-between align-items-center">
            <div>
              <button
                type="button"
                class="btn btn-success btn-sm mr-2"
                :disabled="!participants.length"
                @click="markAllPresent"
              >
                <i class="fas fa-check-double mr-1"></i> All Present
              </button>
              <button
                type="button"
                class="btn btn-danger btn-sm"
                :disabled="!participants.length"
                @click="markAllAbsent"
              >
                <i class="fas fa-times mr-1"></i> All Absent
              </button>
            </div>
            <div>
              <button
                type="button"
                class="btn btn-primary btn-sm mr-2"
                @click="openAddParticipants"
              >
                <i class="fas fa-plus mr-1"></i> Add {{ participantLabel }}s
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :class="{ 'btn-pulse': hasUnsavedChanges }"
                :disabled="!hasUnsavedChanges || attendanceLoading"
                @click="saveAttendance"
              >
                <i
                  class="fas fa-spin fa-circle-notch"
                  v-if="attendanceLoading"
                ></i>
                <i class="fas fa-save mr-1" v-else></i> Save
                <span v-if="unsavedCount > 0">({{ unsavedCount }})</span>
              </button>
            </div>
          </div>
        </div>

        <!-- External Attendance -->
        <div
          class="
            mb-3
            p-3
            border
            rounded
            bg-light
            d-flex
            align-items-center
            justify-content-between
          "
        >
          <div>
            <label class="mb-0 font-weight-bold" for="external-attendance-card"
              >External Attendance</label
            >
            <small class="text-muted d-block"
              >Number of external participants</small
            >
          </div>
          <input
            type="number"
            id="external-attendance-card"
            class="form-control form-control-sm"
            style="width: 120px"
            v-model.number="externalAttendance"
            :disabled="disabled"
            min="0"
            placeholder="0"
          />
        </div>

        <!-- Participant Cards -->
        <div v-if="!participants.length" class="text-center py-4">
          <p class="text-muted">No participants enrolled yet.</p>
          <button
            v-if="!disabled"
            type="button"
            class="btn btn-primary"
            @click="openAddParticipants"
          >
            <i class="fas fa-plus mr-1"></i> Add {{ participantLabel }}s
          </button>
        </div>
        <div v-else class="participant-list">
          <div
            v-for="participant in participants"
            :key="participant.id"
            class="participant-row p-3 mb-2 border rounded"
            :class="{ 'border-warning': isRowChanged(participant.user_id) }"
          >
            <!-- Name Row -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="d-flex align-items-center">
                <a
                  v-if="
                    participant.patient_id && canAccessParticipant(participant)
                  "
                  :href="`/clients/${participant.patient_id}`"
                  target="_blank"
                  class="participant-link"
                >
                  <strong>{{
                    participant.user?.name ||
                    `${participant.user?.last}, ${participant.user?.first}`
                  }}</strong>
                </a>
                <strong
                  v-else
                  v-tooltip="getParticipantNameTooltip(participant)"
                  >{{
                    participant.user?.name ||
                    `${participant.user?.last}, ${participant.user?.first}`
                  }}</strong
                >
              </div>
              <div class="d-flex align-items-center">
                <a
                  v-if="
                    participant.patient_id && canAccessParticipant(participant)
                  "
                  :href="`/clients/${participant.patient_id}/notes`"
                  target="_blank"
                  class="btn btn-sm btn-outline-dark mr-2"
                >
                  <i class="fas fa-file-medical mr-1"></i>Clinical Notes
                </a>
                <span
                  v-if="!disabled"
                  v-tooltip="getRemoveTooltip(participant)"
                  class="d-inline-block"
                >
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeParticipantHandler(participant)"
                    :disabled="
                      removeParticipantLoading === participant.user_id ||
                      !canRemoveParticipant(participant)
                    "
                    :style="
                      !canRemoveParticipant(participant) ||
                      removeParticipantLoading === participant.user_id
                        ? 'pointer-events: none'
                        : ''
                    "
                  >
                    <span
                      v-if="removeParticipantLoading === participant.user_id"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <i v-else class="fas fa-times"></i>
                  </button>
                </span>
              </div>
            </div>

            <!-- Attendance Buttons -->
            <div
              v-if="!disabled"
              class="btn-group btn-group-sm attendance-btn-group w-100 mb-2"
            >
              <button
                type="button"
                class="btn attendance-btn"
                :class="
                  getAttendance(participant.user_id) === 'PRESENT'
                    ? 'btn-success active'
                    : 'btn-present-inactive'
                "
                @click="setAttendance(participant.user_id, 'PRESENT')"
              >
                <i class="fas fa-check mr-1"></i> Present
              </button>
              <button
                type="button"
                class="btn attendance-btn"
                :class="
                  getAttendance(participant.user_id) === 'ABSENT'
                    ? 'btn-danger active'
                    : 'btn-absent-inactive'
                "
                @click="setAttendance(participant.user_id, 'ABSENT')"
              >
                <i class="fas fa-times mr-1"></i> Absent
              </button>
            </div>
            <div v-else class="mb-2">
              <span
                class="badge"
                :class="
                  getAttendance(participant.user_id) === 'PRESENT'
                    ? 'badge-deep-success'
                    : getAttendance(participant.user_id) === 'ABSENT'
                    ? 'badge-danger'
                    : 'badge-secondary'
                "
                style="padding: 6px 10px"
              >
                {{ getAttendance(participant.user_id) }}
              </span>
            </div>

            <!-- Fee Toggle + Notes -->
            <div :class="isMobile ? '' : 'row mt-2'">
              <div
                :class="isMobile ? 'mb-2' : 'col-4 d-flex align-items-center'"
              >
                <div class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="`fee-${participant.id}`"
                    :checked="getFeeRequired(participant.user_id)"
                    @change="
                      setFeeRequired(participant.user_id, $event.target.checked)
                    "
                    :disabled="disabled"
                  />
                  <label
                    class="custom-control-label"
                    :for="`fee-${participant.id}`"
                    >Fee Required</label
                  >
                </div>
              </div>
              <div :class="isMobile ? '' : 'col-8'">
                <input
                  type="text"
                  :class="`form-control form-control-sm${
                    isMobile ? ' mt-3' : ''
                  }`"
                  :value="getAttendanceNotes(participant.user_id)"
                  @input="
                    setAttendanceNotes(participant.user_id, $event.target.value)
                  "
                  placeholder="Attendance notes..."
                  :disabled="disabled"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Save Attendance Button (bottom) -->
        <div class="mt-3" v-if="!disabled">
          <button
            type="button"
            class="btn btn-block btn-primary"
            :class="{ 'btn-pulse': hasUnsavedChanges }"
            @click="saveAttendance"
            :disabled="!hasUnsavedChanges || attendanceLoading"
          >
            <span
              v-if="attendanceLoading"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            <i v-else class="fas fa-save mr-1"></i> Save Attendance
            <span v-if="unsavedCount > 0">({{ unsavedCount }} changes)</span>
          </button>
        </div>
      </template>

      <!-- ==================== TABLE LAYOUT ==================== -->
      <template v-else>
        <!-- Bulk Actions (full-size) -->
        <div
          v-if="!disabled"
          class="
            mb-3
            d-flex
            flex-column flex-md-row
            justify-content-between
            align-items-stretch align-items-md-center
          "
        >
          <div class="mb-2 mb-md-0 d-flex flex-column flex-md-row">
            <button
              type="button"
              class="btn btn-success w-100 w-md-auto mb-2 mb-md-0 mr-md-2"
              :disabled="!participants.length"
              @click="markAllPresent"
            >
              <i class="fas fa-check-double mr-1"></i> Mark All Present
            </button>
            <button
              type="button"
              class="btn btn-danger w-100 w-md-auto"
              :disabled="!participants.length"
              @click="markAllAbsent"
            >
              <i class="fas fa-times mr-1"></i> Mark All Absent
            </button>
          </div>
          <button
            type="button"
            class="btn btn-primary w-100 w-md-auto"
            :class="{ 'btn-pulse': hasUnsavedChanges }"
            :disabled="attendanceLoading || !hasUnsavedChanges"
            @click="saveAttendance"
          >
            <i class="fas fa-spin fa-circle-notch" v-if="attendanceLoading"></i>
            <i class="fas fa-save mr-1" v-else></i>
            Save Attendance
            <span v-if="unsavedCount > 0" class="ml-1">
              ({{ unsavedCount }} change{{ unsavedCount > 1 ? "s" : "" }})
            </span>
          </button>
        </div>

        <!-- External Attendance -->
        <div
          class="
            mb-3
            p-3
            border
            rounded
            bg-light
            d-flex
            align-items-center
            justify-content-between
          "
        >
          <div>
            <label class="mb-0 font-weight-bold" for="external-attendance-table"
              >External Attendance</label
            >
            <small class="text-muted d-block"
              >Number of external participants</small
            >
          </div>
          <input
            type="number"
            id="external-attendance-table"
            class="form-control form-control-sm"
            style="width: 120px"
            v-model.number="externalAttendance"
            :disabled="disabled"
            min="0"
            placeholder="0"
          />
        </div>

        <!-- Participants Table -->
        <div v-if="!participants.length" class="text-center py-4">
          <p class="text-muted">No participants enrolled yet.</p>
          <button
            v-if="!disabled"
            type="button"
            class="btn btn-primary"
            @click="openAddParticipants"
          >
            <i class="fas fa-plus mr-1"></i> Add {{ participantLabel }}s
          </button>
        </div>
        <div v-else class="table-container">
          <table
            class="table table-striped mb-0 responsive-mobile-tablet-table"
          >
            <thead>
              <tr>
                <th>Participant</th>
                <th>Fee Required</th>
                <th>Attendance</th>
                <th>Notes</th>
                <th v-if="!disabled">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="participant in participants"
                :key="participant.id"
                class="shadow-sm"
                :class="{
                  'row-changed': isRowChanged(participant.user_id),
                }"
              >
                <td>
                  <b class="mobile-tablet-only mr-2">Participant:</b>
                  <a
                    v-if="
                      participant.patient_id &&
                      canAccessParticipant(participant)
                    "
                    :href="`/clients/${participant.patient_id}`"
                    target="_blank"
                    class="participant-link"
                  >
                    {{ participant.user?.name }}
                  </a>
                  <span
                    v-else
                    v-tooltip="getParticipantNameTooltip(participant)"
                    >{{ participant.user?.name }}</span
                  >
                  <a
                    v-if="
                      participant.patient_id &&
                      canAccessParticipant(participant)
                    "
                    :href="`/clients/${participant.patient_id}/notes`"
                    target="_blank"
                    class="ml-2 small text-dark"
                  >
                    <i class="fas fa-file-medical"></i> Clinical Notes
                  </a>
                </td>
                <td>
                  <b class="mobile-tablet-only mr-2">Fee Required:</b>
                  <div class="custom-control custom-switch d-inline-block">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      :id="`fee-${participant.id}`"
                      :checked="getFeeRequired(participant.user_id)"
                      @change="
                        setFeeRequired(
                          participant.user_id,
                          $event.target.checked
                        )
                      "
                      :disabled="disabled"
                    />
                    <label
                      class="custom-control-label"
                      :for="`fee-${participant.id}`"
                    ></label>
                  </div>
                </td>
                <td>
                  <b class="mobile-tablet-only mr-2">Attendance:</b>
                  <div class="btn-group attendance-btn-group" v-if="!disabled">
                    <button
                      type="button"
                      class="btn attendance-btn"
                      :class="
                        getAttendance(participant.user_id) === 'PRESENT'
                          ? 'btn-success active'
                          : 'btn-present-inactive'
                      "
                      @click="setAttendance(participant.user_id, 'PRESENT')"
                    >
                      <i class="fas fa-check mr-1"></i> Present
                    </button>
                    <button
                      type="button"
                      class="btn attendance-btn"
                      :class="
                        getAttendance(participant.user_id) === 'ABSENT'
                          ? 'btn-danger active'
                          : 'btn-absent-inactive'
                      "
                      @click="setAttendance(participant.user_id, 'ABSENT')"
                    >
                      <i class="fas fa-times mr-1"></i> Absent
                    </button>
                  </div>
                  <span
                    v-else
                    :class="
                      getAttendanceBadgeClass(participant.attendance_status)
                    "
                  >
                    {{ participant.attendance_status }}
                  </span>
                </td>
                <td>
                  <b class="mobile-tablet-only mr-2">Notes:</b>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :value="getAttendanceNotes(participant.user_id)"
                    @input="
                      setAttendanceNotes(
                        participant.user_id,
                        $event.target.value
                      )
                    "
                    placeholder="Attendance notes..."
                    :disabled="disabled"
                    v-if="!disabled"
                  />
                  <span v-else>{{ participant.attendance_notes || "-" }}</span>
                </td>
                <td v-if="!disabled" class="td-actions">
                  <div
                    class="
                      d-flex
                      justify-content-center justify-content-xl-start
                    "
                  >
                    <span
                      v-tooltip="getRemoveTooltip(participant)"
                      class="d-inline-block"
                    >
                      <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removeParticipantHandler(participant)"
                        :disabled="
                          removeParticipantLoading === participant.user_id ||
                          !canRemoveParticipant(participant)
                        "
                        :title="getRemoveTooltip(participant)"
                        :style="
                          !canRemoveParticipant(participant) ||
                          removeParticipantLoading === participant.user_id
                            ? 'pointer-events: none'
                            : ''
                        "
                      >
                        <i
                          class="fas fa-spin fa-circle-notch"
                          v-if="
                            removeParticipantLoading === participant.user_id
                          "
                        ></i>
                        <span v-else>Remove</span>
                      </button>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Save Attendance Button -->
        <div class="text-right mt-3" v-if="!disabled">
          <button
            type="button"
            class="btn btn-primary btn-lg w-100 w-md-auto"
            :class="{ 'btn-pulse': hasUnsavedChanges }"
            @click="saveAttendance"
            :disabled="attendanceLoading || !hasUnsavedChanges"
          >
            <i class="fas fa-spin fa-circle-notch" v-if="attendanceLoading"></i>
            <i class="fas fa-save mr-1" v-else></i> Save Attendance
            <span v-if="unsavedCount > 0" class="ml-1">
              ({{ unsavedCount }} change{{ unsavedCount > 1 ? "s" : "" }})
            </span>
          </button>
        </div>
      </template>
    </div>

    <!-- Add Participants Modal -->
    <add-participants-modal
      v-if="triggeredSession"
      :session="triggeredSession"
      @participants-added="participantsAddedHandler"
      v-model="showModal"
    />
  </div>
</template>

<script>
import AddParticipantsModal from "@/components/group-session/AddParticipantsModal.vue";
import attendanceDataMixin from "@/components/group-session/mixins/attendanceDataMixin";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

dayjs.extend(utc);
dayjs.extend(timezone);

export default {
  name: "AttendanceManager",
  mixins: [attendanceDataMixin],
  components: {
    AddParticipantsModal,
  },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    sessionId: {
      type: [Number, String],
      required: true,
    },
    session: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    participantLabel: {
      type: String,
      default: "Client",
    },
    showSuccessToast: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      triggeredSession: null,
      eligibleParticipantIds: [],
      isMobile: false,
      showModal: false,
    };
  },
  computed: {
    ...mapState({
      participants: (state) => state.groupSessions.participants.data,
      participantsLoading: (state) => state.groupSessions.participants.loading,
      removeParticipantLoading: (state) =>
        state.groupSessions.removeParticipantLoading,
      attendanceLoading: (state) => state.groupSessions.attendanceLoading,
    }),
    useCardLayout() {
      return this.compact || this.isMobile;
    },
    isFutureSession() {
      return (
        this.session?.start_time &&
        dayjs(this.session.start_time)
          .tz("America/Toronto")
          .isAfter(dayjs().tz("America/Toronto"))
      );
    },
  },
  watch: {
    hasUnsavedChanges(val) {
      this.$emit("unsaved-changes", val);
    },
    session: {
      handler(val) {
        this.initExternalAttendance(val?.external_attendance);
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadParticipants();
    this.loadEligibleParticipants();
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    ...mapActions({
      getParticipants: "groupSessions/getParticipants",
      bulkUpdateAttendance: "groupSessions/bulkUpdateAttendance",
      removeParticipantAction: "groupSessions/removeParticipant",
      getEligibleParticipants: "groupSessions/getEligibleParticipants",
    }),
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },
    async loadParticipants() {
      await this.getParticipants(this.sessionId);
    },
    async loadEligibleParticipants() {
      const eligibleClients = await this.getEligibleParticipants({
        sessionId: this.sessionId,
        includeEnrolled: true,
      });
      this.eligibleParticipantIds = (eligibleClients || []).map((c) => c.id);
    },
    canRemoveParticipant(participant) {
      if (this.isAdmin) {
        return true;
      }
      return this.eligibleParticipantIds.includes(participant.user_id);
    },
    canAccessParticipant(participant) {
      if (this.isAdmin) return true;
      return this.eligibleParticipantIds.includes(participant.user_id);
    },
    getParticipantNameTooltip(participant) {
      if (!participant.patient_id) return null;
      if (!this.canAccessParticipant(participant)) {
        return "This attendee is not assigned to you";
      }
      return null;
    },
    getRemoveTooltip(participant) {
      if (!this.canRemoveParticipant(participant)) {
        return "You can only remove your own attendees from this session";
      }
      return "Remove attendee";
    },
    getAttendanceBadgeClass(status) {
      const classMap = {
        SCHEDULED: "badge badge-secondary",
        PRESENT: "badge badge-deep-success",
        ABSENT: "badge badge-danger",
      };
      return classMap[status] || "badge badge-secondary";
    },
    openAddParticipants() {
      this.triggeredSession = this.session;
      this.$nextTick(() => {
        this.showModal = true;
      });
    },
    async participantsAddedHandler() {
      await this.loadParticipants();
      await this.loadEligibleParticipants();
      this.$emit("participants-added");
      this.$emit("participant-count-changed", {
        sessionId: this.sessionId,
        count: this.participants.length,
      });
    },
    async saveAttendance() {
      const participants = Object.values(this.attendanceData).map((p) => ({
        ...p,
        attendance_notes: p.attendance_notes || null,
      }));
      const result = await this.bulkUpdateAttendance({
        sessionId: this.sessionId,
        participants,
        externalAttendance: this.externalAttendance,
      });
      if (result) {
        this.resetAttendanceData();
        await this.loadParticipants();
        if (this.showSuccessToast) {
          Swal.fire({
            title: "Success",
            text: "Attendance saved successfully",
            icon: "success",
            timer: 2000,
          });
        }
        this.$emit("saved");
      }
    },
    async removeParticipantHandler(participant) {
      const name =
        participant.user?.name ||
        `${participant.user?.last}, ${participant.user?.first}`;

      const wasInvited = !!participant.invitation_email_sent_at;
      const showNotifyOption = wasInvited && this.isFutureSession;

      let sendNotification = false;
      let confirmed = false;

      if (showNotifyOption) {
        const result = await Swal.fire({
          title: "Remove Participant?",
          text: `Are you sure you want to remove ${name}?`,
          icon: "warning",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Remove & Notify",
          denyButtonText: "Remove Only",
          cancelButtonText: "Cancel",
          customClass: {
            confirmButton: "btn btn-danger ml-2",
            denyButton: "btn btn-black ml-2",
            cancelButton: "btn btn-outline-danger ml-2",
          },
          buttonsStyling: false,
        });
        if (result.isConfirmed) {
          sendNotification = true;
          confirmed = true;
        } else if (result.isDenied) {
          confirmed = true;
        }
      } else {
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            confirmButton: "btn btn-black ml-4",
            cancelButton: "btn btn-danger",
          },
          buttonsStyling: false,
        });
        const result = await swalWithBootstrapButtons.fire({
          title: "Remove Participant?",
          text: `Are you sure you want to remove ${name}?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, remove",
          cancelButtonText: "Cancel",
          reverseButtons: true,
        });
        confirmed = result.isConfirmed;
      }

      if (confirmed) {
        const success = await this.removeParticipantAction({
          sessionId: this.sessionId,
          userId: participant.user_id,
          sendNotification,
        });
        if (success) {
          await this.loadParticipants();
          await this.loadEligibleParticipants();
          this.$emit("participant-removed");
          this.$emit("participant-count-changed", {
            sessionId: this.sessionId,
            count: this.participants.length,
          });
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.participant-link {
  color: #007bff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #0056b3;
    text-decoration: underline;
  }
}

// ==================== CARD ROWS LAYOUT ====================
.participant-row {
  background-color: #fafafa;
  transition: border-color 0.2s;
}

.participant-row.border-warning {
  border-width: 2px !important;
}

.participant-list {
  @media (max-width: 768px) {
    max-height: 260px;
  }
  max-height: 330px;
  overflow-y: auto;
}

// Compact attendance button sizing
.attendance-btn-group .attendance-btn {
  padding: 0.35rem 0.6rem;
  font-size: 0.85rem;
}

// ==================== TABLE LAYOUT ====================
.row-changed {
  background-color: #fff3cd !important;
  border-left: 3px solid #ffc107;
  animation: highlight-fade 0.3s ease-in;
}

@keyframes highlight-fade {
  from {
    background-color: #ffe69c;
  }
  to {
    background-color: #fff3cd;
  }
}

.btn-pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 123, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}

.table {
  font-size: 1rem;

  th {
    font-size: 1rem;
    font-weight: 600;
  }

  td {
    font-size: 1rem;
    vertical-align: middle;
  }
}

// Table layout attendance buttons (larger)
.table .attendance-btn-group {
  .attendance-btn {
    padding: 0.5rem 1rem;
    font-size: 0.95rem;
    font-weight: 500;
    min-width: 100px;
  }
}

// ==================== SHARED ====================
.attendance-btn-group {
  .btn-present-inactive {
    background-color: #e8f5e9;
    border-color: #a5d6a7;
    color: #2e7d32;

    &:hover {
      background-color: #c8e6c9;
      border-color: #81c784;
    }
  }

  .btn-absent-inactive {
    background-color: #ffebee;
    border-color: #ef9a9a;
    color: #c62828;

    &:hover {
      background-color: #ffcdd2;
      border-color: #e57373;
    }
  }

  .btn-success.active,
  .btn-danger.active {
    font-weight: 600;
  }
}

.w-md-auto {
  @media (min-width: 768px) {
    width: auto !important;
  }
}

// ==================== MOBILE OVERRIDES ====================
@media (max-width: 767px) {
  .table {
    font-size: 0.95rem;

    th,
    td {
      font-size: 0.95rem;
    }
  }

  .attendance-btn-group {
    flex-direction: row;

    .attendance-btn {
      padding: 0.35rem 0.6rem;
      font-size: 0.8rem;
      min-width: auto;
    }
  }
}
</style>
